import React from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { useManagementPackContext } from './ManagementPackContext';
import { SettingRow } from './tabs/FormField';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import TextButton from 'CommonComponents/TextButton';
import ToggleInput from 'CommonComponents/ToggleInput';

const saveSchema = () =>
    Yup.object({
        name: Yup.string().trim().required($t('Enter a name for the configuration.')).max(255),
    });

const setDetails = (set, applied) =>
    [set.is_default ? $t('Default') : null, set.is_shared ? $t('Shared') : $t('Personal'), applied ? $t('In use') : null].filter(Boolean).join(' · ');

const SavedSetRow = ({ set, applied }) => {
    const { options, readOnly, loadSavedSet, setDefaultSet } = useManagementPackContext();

    return (
        <li className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-3 tw-border-b tw-border-charcoal-15">
            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                <span className="tw-text-sm tw-font-semibold tw-text-charcoal tw-truncate">{set.name}</span>
                <span className="tw-text-xs tw-text-charcoal-75">{setDetails(set, applied)}</span>
                {options.canManageSharedSets && set.is_shared && !set.is_default && (
                    <TextButton className="tw-self-start" onClick={() => setDefaultSet(set)}>
                        {$t('Set as default for this event')}
                    </TextButton>
                )}
            </div>
            <TextButton onClick={() => loadSavedSet(set)} disabled={readOnly} aria-label={$t('Load {{name}}', { name: set.name })}>
                {$t('Load')}
            </TextButton>
        </li>
    );
};

/**
 * Saved configurations for the pack: load one into the builder, save the
 * current settings under a name, and choose the event's default.
 */
const SavedConfigurations = () => {
    const { options, savedSets, appliedFilterSetId, saveCurrentAsSet } = useManagementPackContext();

    return (
        <div className="tw-pt-5">
            <h3 className="tw-text-xs tw-font-semibold tw-text-charcoal-75 tw-m-0">{$t('Saved configurations')}</h3>
            <p className="tw-text-xs tw-text-charcoal-75 tw-mt-1 tw-mb-0">
                {$t("A configuration saves every section and setting in this builder, so next month's pack starts from the same setup.")}
            </p>

            {savedSets.length ? (
                <ul className="tw-list-none tw-m-0 tw-p-0">
                    {savedSets.map((set) => (
                        <SavedSetRow key={set.id} set={set} applied={set.id === appliedFilterSetId} />
                    ))}
                </ul>
            ) : (
                <p className="tw-text-sm tw-text-charcoal-75 tw-m-0 tw-py-3 tw-border-b tw-border-charcoal-15">{$t('No saved configurations yet.')}</p>
            )}

            <Formik
                initialValues={{ name: '', isShared: false, isDefault: false }}
                validationSchema={saveSchema()}
                onSubmit={(formValues, actions) => {
                    const isShared = options.canManageSharedSets && formValues.isShared;

                    return saveCurrentAsSet({ name: formValues.name.trim(), isShared, isDefault: isShared && formValues.isDefault })
                        .then(() => actions.resetForm())
                        .catch(() => {})
                        .finally(() => actions.setSubmitting(false));
                }}
            >
                {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue, handleBlur }) => (
                    <div className="tw-flex tw-flex-col">
                        <SettingRow
                            label={$t('Save current as')}
                            help={$t('Name this configuration')}
                            htmlFor="mp-saved-set-name"
                            error={touched.name && errors.name}
                        >
                            <input
                                id="mp-saved-set-name"
                                name="name"
                                type="text"
                                className="text-input tw-border-0 tw-w-full"
                                maxLength={255}
                                placeholder={$t('e.g. Board pack')}
                                value={values.name}
                                onBlur={handleBlur}
                                onChange={(event) => setFieldValue('name', event.target.value)}
                            />
                        </SettingRow>

                        {options.canManageSharedSets && (
                            <>
                                <div className="tw-py-3">
                                    <CheckboxInput
                                        label={$t('Share with everyone on this event')}
                                        checked={values.isShared}
                                        onChange={() => {
                                            setFieldValue('isShared', !values.isShared);
                                            setFieldValue('isDefault', false);
                                        }}
                                    />
                                </div>
                                <SettingRow label={$t('Set as default for this event')} help={$t('Shared configurations only')}>
                                    <div className="tw-flex tw-justify-end">
                                        <ToggleInput
                                            label={<span className="tw-sr-only">{$t('Set as default for this event')}</span>}
                                            noBottomSpacing
                                            checked={values.isDefault}
                                            disabled={!values.isShared}
                                            onChange={() => setFieldValue('isDefault', !values.isDefault)}
                                        />
                                    </div>
                                </SettingRow>
                            </>
                        )}

                        <div className="tw-flex tw-justify-end tw-pt-3">
                            <Button variant="secondary" small onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
                                {$t('Save configuration')}
                            </Button>
                        </div>
                    </div>
                )}
            </Formik>
        </div>
    );
};

export default SavedConfigurations;
